import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { PasswordInput } from "../../components/site/PasswordInput";
import { api, fmtDate, formatErr } from "../../lib/api";
import { Empty, Field, Online, PageHeader, StatusBadge, inputCls } from "../kit";

function UserDetail({ id, role, onChanged }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const load = useCallback(() => api.get(`/admin/users/${id}`).then((r) => { setD(r.data); setF({ ...r.data.user, password: "" }); }), [id]);
  useEffect(() => {
    load();
  }, [load]);
  if (!d) return null;
  const save = async (body) => {
    try {
      await api.put(`/admin/users/${id}`, body);
      toast.success("Saved");
      load();
      onChanged();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const message = async () => {
    const { data } = await api.post("/conversations/direct", { user_id: id });
    nav(`/dashboard/messages?c=${data.id}`);
  };
  const u = d.user;
  return (
    <div className="space-y-5" data-testid="user-detail">
      <div className="flex flex-wrap items-center gap-3"><Online on={u.online} /><span className="text-sm text-slate-300">{u.online ? "Online now" : "Offline"}</span><StatusBadge s={u.status} />
        <button onClick={message} className="btn-ghost !py-1.5 !px-3 text-sm ml-auto" data-testid="user-message-btn"><MessageSquare className="w-4 h-4" /> Message</button></div>
      <div className="grid sm:grid-cols-2 gap-3">
        {[["name", "Name"], ["email", "Email"], ["gamertag", "Gamertag"], ["platform", "Platform"], ["phone", "Phone / Discord"]].map(([k, l]) => (
          <Field key={k} label={l}><Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} data-testid={`user-edit-${k}`} /></Field>
        ))}
        {role === "grinder" && <Field label="Reset password"><PasswordInput value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} placeholder="Leave blank to keep" testid="user-reset-password" /></Field>}
        <Field label="Notes" full><Textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} className={inputCls} /></Field>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => save({ name: f.name, email: f.email, gamertag: f.gamertag, platform: f.platform, phone: f.phone, notes: f.notes, ...(f.password ? { password: f.password } : {}) })} className="btn-electric !py-2" data-testid="user-save-btn">Save</button>
        {u.status === "active" ? (
          <button onClick={() => save({ status: role === "grinder" ? "deactivated" : "suspended" })} className="btn-ghost !py-2 !text-rose-300 !border-rose-500/40" data-testid="user-suspend-btn">{role === "grinder" ? "Deactivate" : "Suspend"}</button>
        ) : <button onClick={() => save({ status: "active" })} className="btn-ghost !py-2" data-testid="user-activate-btn">Reactivate</button>}
      </div>
      {role === "customer" && (
        <div><p className="font-cond font-bold uppercase tracking-wider mb-2">Quotations ({d.quotations.length})</p>
          {d.quotations.map((q) => <div key={q.id} className="flex justify-between text-sm py-1.5 border-b border-white/5"><span>{q.number} · {q.service}</span><StatusBadge s={q.status} /></div>)}</div>
      )}
      <div><p className="font-cond font-bold uppercase tracking-wider mb-2">Orders ({d.orders.length})</p>
        {d.orders.map((o) => <div key={o.id} className="flex justify-between text-sm py-1.5 border-b border-white/5"><span>{o.number} · {o.service}</span><StatusBadge s={o.status} /></div>)}</div>
    </div>
  );
}

function NewGrinder({ onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", gamertag: "", platform: "" });
  const save = async () => {
    try {
      await api.post("/admin/grinders", f);
      toast.success("Grinder account created");
      onDone();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="new-grinder-form">
      {[["name", "Name"], ["email", "Email"], ["password", "Temporary password"], ["gamertag", "Gamertag"], ["platform", "Platform"]].map(([k, l]) => (
        <Field key={k} label={l}>{k === "password" ? <PasswordInput value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} testid="grinder-password-input" /> : <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} data-testid={`grinder-${k}-input`} />}</Field>
      ))}
      <div className="sm:col-span-2 flex gap-2"><button onClick={save} className="btn-electric !py-2" data-testid="grinder-create-btn">Create grinder</button></div>
    </div>
  );
}

export default function People({ role }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get("/admin/users", { params: { role, q } }).then((r) => setItems(r.data)), [role, q]);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  return (
    <div data-testid={`people-page-${role}`}>
      <PageHeader title={role === "grinder" ? "Grinders" : "Customers"} desc={`${items.filter((i) => i.online).length} online · ${items.length} total`}>
        <div className="relative w-56"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" /><Input placeholder="Search name, email" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-9`} data-testid="people-search" /></div>
        {role === "grinder" && <button onClick={() => setCreating(true)} className="btn-electric !py-2" data-testid="new-grinder-btn"><Plus className="w-4 h-4" /> New grinder</button>}
      </PageHeader>
      {items.length === 0 ? <Empty text={`No ${role}s found.`} /> : (
        <div className="card-neon !p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="text-xs uppercase tracking-wider text-slate-500"><tr className="border-b border-white/5">{["", "Name", "Email", role === "grinder" ? "Workload" : "Quotes / Orders", "Status", "Joined"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} onClick={() => setSel(u)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" data-testid={`people-row-${u.email}`}>
                  <td className="p-3"><Online on={u.online} /></td><td className="p-3 font-semibold text-white">{u.name}</td><td className="p-3 text-slate-300">{u.email}</td>
                  <td className="p-3 text-slate-300">{role === "grinder" ? `${u.active_orders} active · ${u.completed_orders} done` : `${u.quotes_count} / ${u.orders_count}`}</td>
                  <td className="p-3"><StatusBadge s={u.status} /></td><td className="p-3 text-slate-500">{fmtDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#050a18] border-[#007bff]/40"><DialogTitle className="font-display text-3xl text-metal">{sel?.name}</DialogTitle>{sel && <UserDetail id={sel.id} role={role} onChanged={load} />}</DialogContent>
      </Dialog>
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#050a18] border-[#007bff]/40"><DialogTitle className="font-cond uppercase">Create grinder account</DialogTitle><NewGrinder onDone={() => { setCreating(false); load(); }} /></DialogContent>
      </Dialog>
    </div>
  );
}
